/**
 * MapImporter - Component for importing warehouse map from JSON file
 */

import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  IconButton,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Description as FileIcon,
  Map as MapIcon,
  AccountTree as NavIcon,
  Inventory2 as ElementsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { importWarehouseMap } from '../../services/locationApi';
import { MapImportData } from './pathTypes';

interface MapImporterProps {
  open: boolean;
  onClose: () => void;
  warehouseId: number;
  warehouseName?: string;
  onImportSuccess: () => void;
}

interface ImportPreview {
  name?: string;
  description?: string;
  dimensions: { width: number; height: number };
  elementsCount: number;
  racksCount: number;
  aislesCount: number;
  zonesCount: number;
  shelvesCount: number;
  hasNavigation: boolean;
  nodesCount: number;
  edgesCount: number;
  hasStartPoint: boolean;
  startPointName?: string;
}

const MapImporter: React.FC<MapImporterProps> = ({
  open,
  onClose,
  warehouseId,
  warehouseName,
  onImportSuccess,
}) => {
  useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [file, setFile] = useState<File | null>(null);
  const [mapData, setMapData] = useState<MapImportData | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ locations: number; nodes: number; edges: number } | null>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    setError(null);
    setSuccess(null);
    setLoading(true);
    
    try {
      const text = await selectedFile.text();
      const data: MapImportData = JSON.parse(text);
      
      // Validate basic structure
      if (!data.dimensions || !data.elements) {
        throw new Error('Nieprawidłowy format pliku. Wymagane pola: dimensions, elements');
      }
      
      setMapData(data);
      
      // Generate preview
      const elementsCount = data.elements.length;
      const racksCount = data.elements.filter(e => e.type === 'RACK').length;
      const aislesCount = data.elements.filter(e => e.type === 'AISLE').length;
      const zonesCount = data.elements.filter(e => ['ZONE', 'COLD', 'HAZMAT', 'FLOOR'].includes(e.type)).length;
      const shelvesCount = data.elements.reduce((sum, e) => sum + (e.shelves?.length || 0), 0);
      
      setPreview({
        name: data.name,
        description: data.description,
        dimensions: data.dimensions,
        elementsCount,
        racksCount,
        aislesCount,
        zonesCount,
        shelvesCount,
        hasNavigation: !!data.navigation,
        nodesCount: data.navigation?.nodes?.length || 0,
        edgesCount: data.navigation?.edges?.length || 0,
        hasStartPoint: !!data.startPoint,
        startPointName: data.startPoint?.name,
      });
      
    } catch (err: any) {
      console.error('Error parsing file:', err);
      setError(err.message || 'Nie udało się odczytać pliku');
      setMapData(null);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!mapData) return;
    
    setImporting(true);
    setError(null);
    
    try {
      const result = await importWarehouseMap(warehouseId, mapData);
      setSuccess({
        locations: result.createdLocations,
        nodes: result.createdNodes,
        edges: result.createdEdges,
      });
      
      // Clear file after successful import
      setFile(null);
      setMapData(null);
      setPreview(null);
      
      // Notify parent
      onImportSuccess();
      
    } catch (err: any) {
      console.error('Error importing map:', err);
      setError(err.response?.data?.error || 'Nie udało się zaimportować mapy');
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setMapData(null);
    setPreview(null);
    setError(null);
    setSuccess(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadIcon />
          <Typography variant="h6">
            Import mapy magazynu
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        {/* Warehouse info */}
        <Alert severity="info" sx={{ mb: 2 }}>
          Importujesz mapę do magazynu: <strong>{warehouseName || `#${warehouseId}`}</strong>
        </Alert>
        
        {/* Success message */}
        {success && (
          <Alert 
            severity="success" 
            icon={<SuccessIcon />}
            sx={{ mb: 2 }}
            onClose={() => setSuccess(null)}
          >
            <Typography variant="body2" fontWeight="bold">
              Import zakończony pomyślnie!
            </Typography>
            <Typography variant="body2">
              Utworzono: {success.locations} lokalizacji, {success.nodes} punktów nawigacji, {success.edges} połączeń
            </Typography>
          </Alert>
        )}
        
        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {/* File upload area */}
        {!file && !success && (
          <Paper
            sx={{
              p: 4,
              textAlign: 'center',
              bgcolor: 'action.hover',
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.selected',
              },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Kliknij lub przeciągnij plik JSON
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Format: JSON z definicją elementów mapy i opcjonalnym grafem nawigacji
            </Typography>
          </Paper>
        )}
        
        {/* Loading indicator */}
        {loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Analizowanie pliku...
            </Typography>
          </Box>
        )}
        
        {/* Preview */}
        {preview && !success && (
          <Box>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <FileIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">
                  {file?.name}
                </Typography>
                <IconButton size="small" onClick={handleReset}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              
              {preview.name && (
                <Typography variant="body2" gutterBottom>
                  <strong>Nazwa:</strong> {preview.name}
                </Typography>
              )}
              
              {preview.description && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {preview.description}
                </Typography>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <MapIcon color="action" />
                <Typography variant="body2">
                  Wymiary: {preview.dimensions.width} x {preview.dimensions.height} px
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ElementsIcon color="action" />
                <Typography variant="body2">
                  Elementy: {preview.elementsCount}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip size="small" label={`${preview.racksCount} regałów`} variant="outlined" />
                <Chip size="small" label={`${preview.aislesCount} alejek`} variant="outlined" />
                <Chip size="small" label={`${preview.zonesCount} stref`} variant="outlined" />
                {preview.shelvesCount > 0 && (
                  <Chip size="small" label={`${preview.shelvesCount} półek`} variant="outlined" color="primary" />
                )}
              </Box>
              
              {preview.hasNavigation && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NavIcon color="success" />
                  <Typography variant="body2" color="success.main">
                    Graf nawigacji: {preview.nodesCount} punktów, {preview.edgesCount} połączeń
                  </Typography>
                </Box>
              )}
              
              {!preview.hasNavigation && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NavIcon color="disabled" />
                  <Typography variant="body2" color="text.secondary">
                    Brak grafu nawigacji (można dodać ręcznie po imporcie)
                  </Typography>
                </Box>
              )}
              
              {preview.hasStartPoint && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4caf50', border: '2px solid #fff', boxShadow: 1 }} />
                  <Typography variant="body2" color="success.main">
                    Punkt startowy: {preview.startPointName || 'Biuro'}
                  </Typography>
                </Box>
              )}
            </Paper>
            
            {importing && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Importowanie mapy...
                </Typography>
                <LinearProgress />
              </Box>
            )}
          </Box>
        )}
        
        {/* Instructions */}
        {!file && !success && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Wspierane formaty: JSON według schematu aplikacji.
              Możesz użyć przykładowego pliku z <code>src/data/exampleWarehouseMap.json</code>
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>
          {success ? 'Zamknij' : 'Anuluj'}
        </Button>
        {preview && !success && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={importing}
            startIcon={importing ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
          >
            {importing ? 'Importowanie...' : 'Importuj mapę'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MapImporter;
